import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import minMax from "dayjs/plugin/minMax";
import { Tithi } from "@/globalTypes";
import { noto_sans } from "@/globalConstants";
import styled from "@emotion/styled";
import { forwardRef, useContext } from "react";
import { MenuButton as BaseMenuButton } from "@mui/base/MenuButton";
import { MenuItem as BaseMenuItem, menuItemClasses } from "@mui/base/MenuItem";
import { CssTransition } from "@mui/base/Transitions";
import { MenuListboxSlotProps } from "@mui/base/Menu/Menu.types";
import { PopupContext } from "@mui/base/Unstable_Popup/PopupContext";

dayjs.extend(utc);
dayjs.extend(minMax);

export const regex = /^[a-zA-Z\s]+$/;
export const formatMyStringAsSlug = (text: string) =>
  text
    .replace(/[^a-zA-Z\s]/g, "") // Filter out non-alphabetic and non-space characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .toLowerCase();
export const getFormattedTime = (time: number) => dayjs(time * 1000).format("hh:mm A");
export const getMoonFace = (tithis: Tithi[], paksha: string) => {
  const tithiWithMaxDiff = tithis.reduce((maxTithi, tithi) =>
    tithi.end_time - tithi.start_time > maxTithi.end_time - maxTithi.start_time ? tithi : maxTithi
  );
  return `${tithiWithMaxDiff.key.toLowerCase()}_${paksha.split(" ")[0].toLowerCase()}`;
};
export const getFormattedDate = (
  date: Date,
  showDay?: boolean,
  uppercase?: boolean,
  showDate?: boolean,
  showMonth?: boolean,
  showYear?: boolean
) => {
  const [dayOfWeek, month, dayOfMonth, year] = date.toDateString().split(" ");
  const ordinalSuffix = (day: string) => {
    const lastDigit = Number(day) % 10;
    const lastTwoDigits = Number(day) % 100;
    return lastTwoDigits > 10 && lastTwoDigits < 14
      ? "th"
      : { 1: "st", 2: "nd", 3: "rd" }[lastDigit] || "th";
  };

  return (
    <div className={`flex gap-1 ${uppercase ? "uppercase" : ""}`}>
      {showDay && <>{dayOfWeek}</>}
      {showDate && (
        <>
          {" " + dayOfMonth}
          {ordinalSuffix(dayOfMonth)}
        </>
      )}
      {showMonth && <>{" " + month}</>}
      {showYear && <>{" " + year}</>}
    </div>
  );
};
export const MenuItem = styled(BaseMenuItem)`
  list-style: none;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:last-of-type {
    border-bottom: none;
  }
  &.${menuItemClasses.disabled} {
    color: grey[400];
  }
`;
export const MenuButton = styled(BaseMenuButton)`
  font-family: ${noto_sans.className};
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5;
  padding: 8px 16px;
  border-radius: 0.2rem;
  color: white;
  transition: all 150ms ease;
  cursor: pointer;
  background: white;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
`;
export const Listbox = styled("ul")`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  padding: 0;
  margin: 4px 0;
  position: relative;
  width: 100%;
  border-radius: 0.2rem;
  overflow: auto;
  outline: 0;
  background: #ffffff;
  border: 1px solid grey[200];
  color: grey[900];

  .closed & {
    opacity: 0;
    transform: scale(0.95, 0.8);
    transition: opacity 200ms ease-in, transform 200ms ease-in;
  }

  .open & {
    opacity: 1;
    transform: scale(1);
    transition: opacity 100ms ease-out, transform 100ms cubic-bezier(0.43, 0.29, 0.37, 1.48);
  }

  .placement-top & {
    transform-origin: bottom;
  }

  .placement-bottom & {
    transform-origin: top;
  }
`;
export const AnimatedListbox = forwardRef(function AnimatedListbox(
  props: MenuListboxSlotProps,
  ref: React.ForwardedRef<HTMLUListElement>
) {
  const { ...other } = props;
  const popupContext = useContext(PopupContext);

  if (!popupContext) {
    throw new Error(
      "The `AnimatedListbox` component cannot be rendered outside a `Popup` component"
    );
  }

  const verticalPlacement = popupContext.placement.split("-")[0];

  return (
    <CssTransition
      className={`placement-${verticalPlacement}`}
      enterClassName="open"
      exitClassName="closed"
    >
      <Listbox {...other} ref={ref} />
    </CssTransition>
  );
});
export const parseDate = (dateString: string) => dayjs(dateString, "MM-DD-YYYY", true).valueOf();
export const checkDate = (selectedDate: Date) => {
  const today = new Date();
  return (
    selectedDate.getDate() === today.getDate() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getFullYear() === today.getFullYear()
  );
};
